/**
 * Expiring Batches Alert Tab Component
 *
 * Displays a detailed list of expiring batches with filtering and quick actions.
 * Implements US-BATCH-006 acceptance criteria.
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-006
 */

import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  Eye,
  // Trash2,
  Phone,
  Zap,
  X,
  Download,
  Search,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface ExpiringBatch {
  id: number;
  batchNumber: string;
  productType: string;
  availableQuantity: number;
  currentQuantity: number;
  quantityUnit: string;
  qualityGrade: string;
  purityPercentage: number;
  expiryDate: string;
  daysRemaining: number;
  urgencyLevel: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  storageLocation: string;
  pesoCompliant: boolean;
  pesoCertificateNumber?: string;
  productionDate: string;
}

interface ExpiringBatchesAlertTabProps {
  batches: ExpiringBatch[];
  loading?: boolean;
  onViewDetails?: (batch: ExpiringBatch) => void;
  onCreateDiscountOrder?: (batch: ExpiringBatch) => void;
  onMarkForDisposal?: (batch: ExpiringBatch) => void;
  onContactSales?: (batch: ExpiringBatch) => void;
}

// Urgency level colors and styling
const urgencyConfig = {
  CRITICAL: { bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-100 text-red-800', icon: AlertTriangle, label: 'Critical' },
  HIGH: { bg: 'bg-yellow-50', border: 'border-yellow-200', badge: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle, label: 'Warning' },
  MEDIUM: { bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-100 text-green-800', icon: Clock, label: 'Normal' },
  LOW: { bg: 'bg-gray-50', border: 'border-gray-200', badge: 'bg-gray-100 text-gray-800', icon: Clock, label: 'Low' },
};

export const ExpiringBatchesAlertTab: React.FC<ExpiringBatchesAlertTabProps> = ({
  batches = [],
  loading = false,
  onViewDetails,
  onCreateDiscountOrder,
  onMarkForDisposal,
  onContactSales,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState<string>('all');
  const [productTypeFilter, setProductTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'days-asc' | 'days-desc' | 'date-asc'>('days-asc');

  // Get unique product types for filter
  const productTypes = useMemo(
    () => [...new Set(batches.map(b => b.productType))],
    [batches]
  );

  // Filter and sort batches
  const filteredBatches = useMemo(() => {
    let filtered = batches.filter(batch => {
      // Search filter — match batch number, product type, quality grade, or storage location
      if (searchTerm) {
        const term = searchTerm.trim().toLowerCase();
        const matches =
          batch.batchNumber?.toLowerCase().includes(term) ||
          batch.productType?.toLowerCase().includes(term) ||
          batch.qualityGrade?.toLowerCase().includes(term) ||
          batch.storageLocation?.toLowerCase().includes(term);
        if (!matches) return false;
      }

      // Urgency filter
      if (urgencyFilter !== 'all' && batch.urgencyLevel !== urgencyFilter) {
        return false;
      }

      // Product type filter
      if (productTypeFilter !== 'all' && batch.productType !== productTypeFilter) {
        return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'days-asc':
          return a.daysRemaining - b.daysRemaining; // Shortest days first
        case 'days-desc':
          return b.daysRemaining - a.daysRemaining; // Longest days first
        case 'date-asc':
          return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime(); // Earliest date first
        default:
          return 0;
      }
    });

    return filtered;
  }, [batches, searchTerm, urgencyFilter, productTypeFilter, sortBy]);

  // Summary stats
  const stats = useMemo(() => ({
    total: batches.length,
    critical: batches.filter(b => b.urgencyLevel === 'CRITICAL').length,
    high: batches.filter(b => b.urgencyLevel === 'HIGH').length,
    medium: batches.filter(b => b.urgencyLevel === 'MEDIUM').length,
  }), [batches]);

  const hasFilters = searchTerm || urgencyFilter !== 'all' || productTypeFilter !== 'all';

  return (
    <TooltipProvider>
      <div className="space-y-4">
      {/* Header with Statistics */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-blue-900">Expiring Batches Summary</h3>
          <Badge className="bg-blue-600 text-white text-base px-3 py-1">
            {stats.total} Total
          </Badge>
        </div>
        <div className="grid grid-cols-4 gap-2 text-sm">
          <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
            <div className="font-bold text-red-600 text-lg">{stats.critical}</div>
            <div className="text-xs text-red-700">Critical</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-center">
            <div className="font-bold text-yellow-600 text-lg">{stats.high}</div>
            <div className="text-xs text-yellow-700">Warning</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
            <div className="font-bold text-green-600 text-lg">{stats.medium}</div>
            <div className="text-xs text-green-700">Normal</div>
          </div>
          <div className="bg-blue-50 border border-blue-300 rounded p-2 text-center">
            <div className="font-bold text-blue-600 text-lg">{stats.total}</div>
            <div className="text-xs text-blue-700">All Batches</div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="space-y-3">
        {/* Search Bar */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search by batch number, product type, quality grade, or storage location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
            aria-label="Search expiring batches"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by urgency..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Urgency Levels</SelectItem>
              <SelectItem value="CRITICAL">Critical (0-7 days remaining)</SelectItem>
              <SelectItem value="HIGH">Warning (7-14 days remaining)</SelectItem>
              <SelectItem value="MEDIUM">Normal (15-30 days remaining)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={productTypeFilter} onValueChange={setProductTypeFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by product type..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Product Types</SelectItem>
              {productTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="days-asc">Days Remaining (Urgent First)</SelectItem>
              <SelectItem value="days-desc">Days Remaining (Latest First)</SelectItem>
              <SelectItem value="date-asc">Expiry Date (Earliest First)</SelectItem>
            </SelectContent>
          </Select>

          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSearchTerm('');
                setUrgencyFilter('all');
                setProductTypeFilter('all');
              }}
              className="gap-2"
            >
              <X className="w-4 h-4" />
              Clear Filters
            </Button>
          )}

          <Button variant="outline" size="sm" className="gap-2 ml-auto">
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-600">Loading expiring batches...</div>
        </div>
      ) : filteredBatches.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="font-medium text-gray-700">No batches found</p>
          <p className="text-sm text-gray-600">
            {hasFilters ? 'Try adjusting your filters' : 'All batches are in good condition'}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-100">
              <TableRow>
                <TableHead className="w-32">Batch Number</TableHead>
                <TableHead>Product Type</TableHead>
                <TableHead className="text-right">Available Qty</TableHead>
                <TableHead>Quality</TableHead>
                <TableHead>Expiry Date</TableHead>
                <TableHead className="text-center">Days Left</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBatches.map((batch) => {
                const config = urgencyConfig[batch.urgencyLevel];
                const UrgencyIcon = config.icon;

                return (
                  <TableRow
                    key={batch.id}
                    className={cn(
                      "border-b hover:bg-gray-50 transition-colors",
                      batch.urgencyLevel === 'CRITICAL' && 'bg-red-50/30'
                    )}
                  >
                    <TableCell className="font-semibold text-blue-600 hover:underline cursor-pointer">
                      {batch.batchNumber}
                    </TableCell>
                    <TableCell>{batch.productType}</TableCell>
                    <TableCell className="text-right font-medium">
                      {batch.availableQuantity.toLocaleString()} {batch.quantityUnit}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{batch.qualityGrade}</Badge>
                    </TableCell>
                    <TableCell>{new Date(batch.expiryDate).toLocaleDateString()}</TableCell>
                    <TableCell className="text-center">
                      <span className={cn(
                        'font-bold px-2 py-1 rounded',
                        batch.daysRemaining < 0 ? 'bg-red-100 text-red-800' :
                          batch.daysRemaining < 7 ? 'bg-red-100 text-red-800' :
                            batch.daysRemaining < 15 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                      )}>
                        {batch.daysRemaining < 0 ? 'Expired' : batch.daysRemaining + ' days'}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={config.badge}>
                        <UrgencyIcon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        {/* View Batch Details - Always Available */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!onViewDetails) {
                                  console.warn('onViewDetails callback not provided');
                                  return;
                                }
                                onViewDetails(batch);
                              }}
                              className="h-8 w-8 p-0 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                              title="View detailed batch information"
                              aria-label={`View details for batch ${batch.batchNumber}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-gray-900 text-white">
                            <p className="text-xs">View Batch Details</p>
                          </TooltipContent>
                        </Tooltip>

                        {/* Create Discount Order - Hidden for now (can be unhidden later if needed) */}
                        {/* <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!onCreateDiscountOrder) {
                                  console.warn('onCreateDiscountOrder callback not provided');
                                  return;
                                }
                                onCreateDiscountOrder(batch);
                              }}
                              className="h-8 w-8 p-0 hover:bg-yellow-100 hover:text-yellow-600 transition-colors"
                              title="Create a discount order to sell this batch quickly"
                              aria-label={`Create discount order for batch ${batch.batchNumber}`}
                            >
                              <Zap className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-gray-900 text-white">
                            <p className="text-xs">Create Discount Order</p>
                            <p className="text-xs text-gray-300">Quick sale option</p>
                          </TooltipContent>
                        </Tooltip> */}

                        {/* Mark for Disposal - Hidden (can be unhidden later if needed) */}
                        {/* <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!onMarkForDisposal) {
                                  console.warn('onMarkForDisposal callback not provided');
                                  return;
                                }
                                onMarkForDisposal(batch);
                              }}
                              className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600 transition-colors"
                              title="Mark this batch for disposal (cannot be reversed)"
                              aria-label={`Mark batch ${batch.batchNumber} for disposal`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-red-900 text-white">
                            <p className="text-xs font-semibold">Mark for Disposal</p>
                            <p className="text-xs text-red-200">⚠️ Cannot be reversed</p>
                          </TooltipContent>
                        </Tooltip> */}

                        {/* Contact Sales Team - Notify about expiring batch */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (!onContactSales) {
                                  console.warn('onContactSales callback not provided');
                                  return;
                                }
                                onContactSales(batch);
                              }}
                              className="h-8 w-8 p-0 hover:bg-green-100 hover:text-green-600 transition-colors"
                              title="Alert sales team about this expiring batch"
                              aria-label={`Notify sales team about batch ${batch.batchNumber}`}
                            >
                              <Phone className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-gray-900 text-white">
                            <p className="text-xs">Notify Sales Team</p>
                            <p className="text-xs text-gray-300">Alert & collaborate</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Result summary */}
      {filteredBatches.length > 0 && (
        <div className="text-sm text-gray-600 text-center pt-2">
          Showing {filteredBatches.length} of {batches.length} batches
        </div>
      )}
      </div>
    </TooltipProvider>
  );
};
